// Publieke herbestelpagina (/herbestellen) — één scherm (ronde 15-09).
//
// De klant komt hier via de beveiligde bestellink (#t= in het
// URL-fragment) of via de site-knop met het bestaande shopklant-token
// (#st=, of nog ?shoptoken= zolang het thema dat stuurt). Geen inlog:
// het token is de autorisatie, met als scope alleen de eigen
// productranglijst en het afrekenen daarvan.
//
// Token uit de URL (veiligheidsronde V1, A7): de pagina leest het
// fragment (of de oude ?token=-query, overgangsperiode van 14 dagen),
// zet het in sessionStorage en wist het meteen uit de adresbalk. Zo
// staat het token nooit in de browsergeschiedenis, nooit in een
// Referer en nooit in een server- of edge-log.
//
// Zonder herkenning (geen token, of een token dat niet meer werkt)
// toont de pagina het aanvraagscherm: één e-mailveld dat een verse
// bestellink laat mailen. De bevestiging daarna is ALTIJD dezelfde zin
// — bekend adres of niet — anders wordt dit scherm een manier om te
// achterhalen wie er klant is.
//
// ── Eén scherm (15-09, mock goedgekeurd) ────────────────────────────
//
// Tot deze ronde was dit een tweestapspagina: bestelling, dan een
// aparte controlepagina met adres, betaalkeuze en betaalknop. Dat is nu
// één scherm waarop de klant alles ziet en in één tik betaalt. Wat dat
// van deze component vraagt:
//
// 1. **Het portaal rekent niets.** Elk bedrag in het afrekenblok komt
//    uit `/offerte` (Lightspeed), via `useOfferte`. De productregels
//    tonen nog wel de catalogusprijs per regel — dat is de prijs die de
//    klant kiest — maar artikelen, bezorging, btw en totaal komen uit
//    de offerte, en die is leidend.
// 2. **Elke wijziging ververst.** Aantal, adres, bezorgkeuze,
//    betaalmethode en kortingscode zitten in `offerteInvoer`; zodra
//    daar iets aan verandert gaat de offerte opnieuw op. Tijdens dat
//    verversen grijzen Bezorging en Totaal en staat de betaalknop uit
//    ("Even geduld…"). Lukt het niet, dan staat er "Dat lukte even
//    niet" met een knop Opnieuw proberen — nooit een stille wachtstand.
// 3. **De knop draagt altijd het bedrag van de laatste offerte.** De
//    `afdruk` van die offerte reist mee naar `/betalen`; geeft de verse
//    offerte daar een ander bedrag, dan weigert de server (409) en
//    rekent er niets af.
//
// Het bezorgadres staat er voluit ("Bezorgen op Straat 12, 1234 AB
// Plaats"). Dat is een bewuste herziening van punt (4) van het besluit
// van 03-09, dat alleen de woonplaats toeliet: dit scherm ís de
// checkout geworden, en je kunt niet controleren waar een bestelling
// heen gaat zonder te zien waar hij heen gaat. De rest van dat besluit
// staat: nooit een telefoonnummer, nooit betaalgegevens, en de link
// waarmee je hier binnenkomt is kortlevend, uniek per klant en na een
// geslaagde betaling dood.
//
// Met de vlag `ideal_afrekenen` uit gedraagt de pagina zich als vóór
// het afrekenen op de pagina zelf: de productlijst met de somregel en
// "Direct afrekenen" naar de webshop-wagen. Die weg is onaangeraakt.
//
// Palet volgt de andere publieke shop-pagina's (retour, tevredenheid)
// en staat in `herbestelStijl.ts`. Mobiel eerst: kaarten stapelen in de
// leesvolgorde van de mock; op desktop staat het afrekenen rechts.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Loader2, Minus, Plus } from "lucide-react";

import "@fontsource/nunito-sans/400.css";
import "@fontsource/nunito-sans/600.css";
import "@fontsource/nunito-sans/700.css";
import "@fontsource/arimo/700.css";

import { herbestelApi } from "@/lib/api";
import {
  domeinVan,
  suggereerEmail,
  type EmailTypfoutConfig,
} from "@/lib/emailTypfouten";
import {
  berekenTotalen,
  doetMee,
  regelTotaalCents,
} from "./herbestelBerekening";
import {
  Adresregel,
  Betaalblok,
  Bezorgblok,
  Kortingsblok,
  Totaalblok,
} from "./EenSchermBlokken";
import { BLAUW, GRIJS, GROEN, ORANJE, PUNTEN_KLEUR, euro } from "./herbestelStijl";
import { useOfferte, type OfferteInvoer } from "./useOfferte";
import type {
  HerbestelAnderAdres,
  HerbestelMeetStap,
  HerbestelPaginaAntwoord,
  HerbestelRegelAntwoord,
} from "@/types/api";
import {
  NovaHulpKaart,
  ProductFoto,
  RETOUR_KAART,
  RETOUR_KOLOM,
  RetourFooter,
  RetourHeader,
  UspBalk,
  VinkjesKaart,
  VinkjesStrip,
} from "../Retour/RetourShopElementen";

const SHOP_URL = "https://www.voeronline.nl";

// De bevestigingspagina (na de bank-redirect) kan hiermee terug naar
// de eigen bestellink zonder het token in een URL of server te delen.
export const HERBESTEL_TOKEN_OPSLAG = "vo-herbestel-token";
// Sleutels in het URL-fragment: #t=<bestellink-token>, #st=<shoptoken>.
export const FRAGMENT_TOKEN = "t";
export const FRAGMENT_SHOPTOKEN = "st";

/**
 * De tokens uit de URL, eenmalig bij het openen (veiligheidsronde V1, A7).
 *
 * Volgorde: fragment (#t=, #st=), dan de oude query (?token=,
 * ?shoptoken=; overgangsperiode van 14 dagen voor mails die nog in
 * omloop zijn), dan het bewaarde token uit sessionStorage (een
 * herlaad van de pagina nadat het fragment gewist is). Zo werkt elke
 * link die vandaag bestaat, en verdwijnt het token toch uit de adresbalk.
 */
export function leesTokensUitUrl(
  search: string,
  hash: string,
): { token: string | null; shopToken: string | null; inUrl: boolean } {
  const fragment = new URLSearchParams(hash.replace(/^#/, ""));
  const query = new URLSearchParams(search);
  const token = fragment.get(FRAGMENT_TOKEN) || query.get("token");
  const shopToken =
    fragment.get(FRAGMENT_SHOPTOKEN) || query.get("shoptoken");
  if (token || shopToken) {
    return { token, shopToken, inUrl: true };
  }
  let bewaard: string | null = null;
  try {
    bewaard = sessionStorage.getItem(HERBESTEL_TOKEN_OPSLAG);
  } catch {
    // Private mode zonder opslag: dan gewoon geen token.
  }
  return { token: bewaard || null, shopToken: null, inUrl: false };
}

const LEEG_ANDER_ADRES: HerbestelAnderAdres = {
  naam: "",
  straat: "",
  huisnummer: "",
  postcode: "",
  plaats: "",
  telefoon: "",
};

// Zoveel eerder bestelde producten staan standaard uitgeklapt onder het
// vaste voer; de rest zit achter "Toon alle N eerder bestelde
// producten". Drie is de grens uit de mock: met de hoofdkaart erbij
// blijft de bestelling op een telefoon binnen één schermhoogte, zodat
// de bezorg- en betaalblokken eronder bereikbaar blijven.
const MAX_SUBREGELS_INGEKLAPT = 3;

// "aanvraag": geen (werkend) token, wél een actief aanvraagscherm.
// "ongeldig": geen herkenning én geen aanvraagscherm — de kale melding.
type Weergave = "laden" | "pagina" | "aanvraag" | "ongeldig";

// Zelfde pragmatische check als de backend: één @, geen spaties, een
// punt in het domein. Alleen om een typefout meteen te melden.
const EMAIL_PATROON = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
function datumKort(iso: string | null): string | null {
  if (!iso) return null;
  const datum = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(datum.getTime())) return null;
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "long",
  }).format(datum);
}

// `minimum` staat standaard op 1, maar het hoofdproduct mag sinds de
// ronde 16-08 wél naar 0: JP liep er live tegenaan dat je juist het
// vaste voer niet uit de bestelling kon halen (de aanvinkregels
// eronder konden dat al wel). Bij 0 telt de regel nergens meer mee —
// zie `gekozenRegels` — en brengt de plus-knop hem in één tik terug.
function Stepper({
  waarde,
  opWijzig,
  label,
  minimum = 1,
}: {
  waarde: number;
  opWijzig: (nieuw: number) => void;
  label: string;
  minimum?: number;
}) {
  return (
    <span
      className="inline-flex items-center overflow-hidden rounded-lg border border-gray-200 bg-white"
      role="group"
      aria-label={label}
    >
      <button
        type="button"
        aria-label={`${label}: minder`}
        className="grid h-10 w-10 place-items-center bg-slate-50 font-bold text-gray-600 disabled:opacity-40"
        disabled={waarde <= minimum}
        onClick={() => opWijzig(Math.max(minimum, waarde - 1))}
      >
        <Minus className="h-4 w-4" />
      </button>
      <span
        aria-live="polite"
        className="min-w-[40px] px-2 text-center text-[15px] font-bold"
      >
        {waarde}
      </span>
      <button
        type="button"
        aria-label={`${label}: meer`}
        className="grid h-10 w-10 place-items-center bg-slate-50 font-bold text-gray-600 disabled:opacity-40"
        disabled={waarde >= 24}
        onClick={() => opWijzig(Math.min(24, waarde + 1))}
      >
        <Plus className="h-4 w-4" />
      </button>
    </span>
  );
}

// Prijskolom van een productregel (spoedronde 16-08): bij aantal 1 de
// kale stukprijs zoals altijd, bij meer stuks de zichtbare rekensom
// "2 × € 66,99" met het regeltotaal er vet onder. Zo kan de balk nooit
// meer een ander bedrag tonen dan de regels laten zien.
function RegelPrijs({
  prijsCents,
  aantal,
  gedempt,
  breed = false,
}: {
  prijsCents: number | null;
  aantal: number;
  gedempt: boolean;
  breed?: boolean;
}) {
  const { t } = useTranslation();
  const bedragKlasse = `block font-bold ${
    gedempt ? "text-gray-400" : "text-gray-900"
  } ${breed ? "text-[16px]" : "text-[14px]"}`;
  return (
    <span
      className={`${breed ? "min-w-[70px]" : "min-w-[60px]"} text-right`}
    >
      {aantal > 1 && (
        <span
          data-testid="regel-rekensom"
          className={`block text-[12px] ${
            gedempt ? "text-gray-400" : "text-gray-500"
          }`}
        >
          {t("herbestel.regel_aantal_maal", {
            aantal,
            prijs: euro(prijsCents),
          })}
        </span>
      )}
      <span className={bedragKlasse}>
        {aantal > 1
          ? euro(regelTotaalCents(prijsCents, aantal))
          : euro(prijsCents)}
      </span>
    </span>
  );
}

/**
 * Eén e-mailveld dat een verse bestellink laat mailen.
 *
 * Na versturen komt er altijd dezelfde bevestiging in beeld, ongeacht
 * wat de server deed: het endpoint antwoordt zelf ook altijd hetzelfde
 * en doet het echte werk pas daarna. Zelfs een netwerkfout leidt hier
 * bewust niet tot een afwijkende melding — dat zou het enige zichtbare
 * verschil tussen adressen kunnen worden.
 */
function AanvraagFormulier({
  compact = false,
  geldigUren,
}: {
  compact?: boolean;
  geldigUren: number;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(false);
  const [verstuurd, setVerstuurd] = useState(false);
  // Veiligheidsronde V1 (A3): vanaf de vierde poging van het etmaal één
  // neutrale tekst, bekend of onbekend adres precies dezelfde.
  const [dagcapBereikt, setDagcapBereikt] = useState(false);
  // Sessie 23: typfout-vangnet op het e-mailveld. De lijst komt uit de
  // publieke config (één bron met de checkout); zonder lijst geen
  // suggestie. Beoordeeld bij het verlaten van het veld, niet per toets.
  const [typfoutConfig, setTypfoutConfig] = useState<EmailTypfoutConfig | null>(
    null,
  );
  const [suggestie, setSuggestie] = useState<string | null>(null);
  const gemeld = useRef<Set<string>>(new Set());
  useEffect(() => {
    let actief = true;
    herbestelApi
      .emailTypfouten()
      .then((config) => {
        if (actief) setTypfoutConfig(config);
      })
      .catch(() => undefined);
    return () => {
      actief = false;
    };
  }, []);
  const meetSuggestie = (
    gebeurtenis:
      | "email_suggestie_getoond"
      | "email_suggestie_geaccepteerd"
      | "email_suggestie_genegeerd",
    adres: string,
  ) => {
    const sleutel = `${gebeurtenis}:${adres}`;
    if (gemeld.current.has(sleutel)) return;
    gemeld.current.add(sleutel);
    herbestelApi.meetEmailSuggestie(gebeurtenis, domeinVan(adres)).catch(() => undefined);
  };
  const beoordeelSuggestie = () => {
    const nieuw = suggereerEmail(email, typfoutConfig);
    setSuggestie(nieuw);
    if (nieuw) meetSuggestie("email_suggestie_getoond", nieuw);
  };
  const neemSuggestieOver = () => {
    if (!suggestie) return;
    const gekozen = suggestie;
    setEmail(gekozen);
    setSuggestie(null);
    setFout(false);
    meetSuggestie("email_suggestie_geaccepteerd", gekozen);
  };

  if (verstuurd) {
    // Spoedronde 16-08: dit is het belangrijkste moment van de pagina,
    // dus een echte succeskaart in de stijl van het retourportaal in
    // plaats van één grijs regeltje. Het adres mag in beeld: de
    // bezoeker typte het zelf net in. De tekst blijft neutraal —
    // bekend of onbekend adres, altijd exact dezelfde kaart.
    return (
      <div
        role="status"
        data-testid="aanvraag-bevestiging"
        className={`text-center ${compact ? "mt-4 py-4" : "py-5"}`}
      >
        <span
          aria-hidden
          className="mx-auto grid h-16 w-16 place-items-center rounded-full"
          style={{ background: GROEN }}
        >
          <Check className="h-8 w-8 text-white" strokeWidth={3} />
        </span>
        <p className="mt-4 text-[22px] font-bold leading-tight text-gray-900">
          {t("herbestel.verstuurd_titel")}
        </p>
        <p
          data-testid="aanvraag-bevestiging-tekst"
          className="mx-auto mt-2 max-w-[430px] text-[14px] leading-relaxed text-[#333333]"
        >
          {dagcapBereikt
            ? t("herbestel.verstuurd_neutraal")
            : t("herbestel.verstuurd_tekst", {
                adres: email.trim(),
                uren: geldigUren,
              })}
        </p>
      </div>
    );
  }

  const versturen = () => {
    if (bezig) return;
    const adres = email.trim();
    if (!EMAIL_PATROON.test(adres)) {
      setFout(true);
      return;
    }
    if (suggestie) meetSuggestie("email_suggestie_genegeerd", suggestie);
    setFout(false);
    setBezig(true);
    herbestelApi
      .aanvraag(adres)
      .then((antwoord) => {
        if (antwoord?.dagcap_bereikt) setDagcapBereikt(true);
      })
      .catch(() => undefined)
      .finally(() => {
        setBezig(false);
        setVerstuurd(true);
      });
  };

  // Stylingronde 16-08: opgebouwd als de Order zoeken-kaart van het
  // retourportaal — label boven het veld, veld op volle breedte, de
  // knop eronder in plaats van ernaast. Op een telefoon scheelt dat een
  // afgeknepen veld naast een halve knop.
  return (
    <form
      data-testid="aanvraag-formulier"
      // Eigen validatie in plaats van de browsermelding: die is niet
      // vertaald en verschilt per browser, en dit is een klantpagina.
      noValidate
      className={compact ? "mt-4" : "mt-1"}
      onSubmit={(e) => {
        e.preventDefault();
        versturen();
      }}
    >
      <label
        htmlFor="herbestel-email"
        className="block text-[14px] font-semibold leading-relaxed text-[#333333]"
      >
        {t("herbestel.aanvraag_uitleg")}
      </label>
      <input
        id="herbestel-email"
        type="email"
        inputMode="email"
        autoComplete="email"
        value={email}
        placeholder={t("herbestel.aanvraag_placeholder")}
        onChange={(e) => {
          setEmail(e.target.value);
          if (fout) setFout(false);
          if (suggestie && suggereerEmail(e.target.value, typfoutConfig) !== suggestie) {
            setSuggestie(null);
          }
        }}
        onBlur={beoordeelSuggestie}
        aria-invalid={fout}
        className="mt-2 block w-full rounded-[10px] border border-gray-300 bg-white px-4 py-3 text-[15px] text-gray-900 outline-none focus:border-gray-400"
      />
      {fout && (
        <p role="alert" className="mt-2 text-[13px] font-semibold text-red-600">
          {t("herbestel.aanvraag_ongeldig_adres")}
        </p>
      )}
      {suggestie && (
        <p
          role="status"
          data-testid="email-suggestie"
          className="mt-2 text-[14px] leading-relaxed text-[#333333]"
        >
          {t("herbestel.suggestie_vraag")}{" "}
          <button
            type="button"
            data-testid="email-suggestie-knop"
            onClick={neemSuggestieOver}
            className="font-bold text-[#1a73a7] underline"
          >
            {suggestie}
          </button>
          ?
        </p>
      )}
      <button
        type="submit"
        data-testid="aanvraag-knop"
        disabled={bezig}
        className="mt-3.5 flex w-full items-center justify-center rounded-[10px] px-6 py-3.5 text-[15px] font-bold text-white disabled:opacity-60"
        style={{ background: ORANJE }}
      >
        {bezig ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          t("herbestel.aanvraag_knop")
        )}
      </button>
    </form>
  );
}
export function HerbestelPagina() {
  const { t } = useTranslation();
  const locatie = useLocation();
  const navigeer = useNavigate();
  // Eenmalig gelezen: daarna wordt de URL geschoond en mag een
  // routerwissel de tokens niet meer veranderen.
  const [tokens] = useState(() =>
    leesTokensUitUrl(locatie.search, locatie.hash),
  );
  const token = tokens.token;
  const shopToken = tokens.shopToken;

  useEffect(() => {
    if (!tokens.inUrl) return;
    // Token uit de adresbalk (A7): zelfde pad, zonder query en zonder
    // fragment; replace zodat het ook niet in de geschiedenis blijft.
    navigeer(
      { pathname: locatie.pathname, search: "", hash: "" },
      { replace: true },
    );
    // Alleen bij het openen van de pagina.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [weergave, setWeergave] = useState<Weergave>("laden");
  // Of het aanvraagscherm aan staat, weten we pas na /stand; tot dan
  // tonen we geen formulier dat misschien niets doet.
  const [aanvraagMogelijk, setAanvraagMogelijk] = useState(false);
  const [geldigUren, setGeldigUren] = useState(4);
  const [data, setData] = useState<HerbestelPaginaAntwoord | null>(null);
  const [aantallen, setAantallen] = useState<Record<string, number>>({});
  const [aangevinkt, setAangevinkt] = useState<Record<string, boolean>>(
    {},
  );
  const [combidealGekozen, setCombidealGekozen] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [afrekenFout, setAfrekenFout] = useState(false);
  const [alleProducten, setAlleProducten] = useState(false);

  // Bezorgadres: "klant" = het bekende adres, "ander" = zelf ingevuld.
  // `adresOpen` is het uitgeklapte formulier achter de knop Wijzig.
  const [adresModus, setAdresModus] = useState<"klant" | "ander">(
    "klant",
  );
  const [adresOpen, setAdresOpen] = useState(false);
  const [anderAdres, setAnderAdres] =
    useState<HerbestelAnderAdres>(LEEG_ANDER_ADRES);
  // Het adres waarop de offerte draait. Pas als de klant het formulier
  // bevestigt gaat het mee — anders zou elke toetsaanslag in het
  // straatveld een nieuwe Lightspeed-offerte opleveren.
  const [bevestigdAdres, setBevestigdAdres] =
    useState<HerbestelAnderAdres | null>(null);
  const [adresFout, setAdresFout] = useState(false);
  const [adresHint, setAdresHint] = useState(false);
  const [betaalFout, setBetaalFout] = useState(false);
  const [betaalVerouderd, setBetaalVerouderd] = useState(false);

  const [methode, setMethode] = useState("IDEAL");
  const [betaalOpen, setBetaalOpen] = useState(false);
  const [bezorgoptie, setBezorgoptie] = useState<string | null>(null);
  // Hotfix 17-09: naast het shipment_id ook de vervoerder die de klant
  // aanklikte. Het id hoort bij één gewichtsklasse en verdwijnt zodra
  // het mandje verandert; met de titel erbij houdt de server de bewuste
  // keuze vast in plaats van stil naar de goedkoopste te springen.
  const [bezorgvervoerder, setBezorgvervoerder] = useState<string | null>(
    null,
  );

  const [kortingOpen, setKortingOpen] = useState(false);
  const [kortingInvoer, setKortingInvoer] = useState("");
  // De code die daadwerkelijk in de offerte zit. Pas bij "Toepassen"
  // gezet: tijdens het typen hoort er niets te verversen.
  const [kortingCode, setKortingCode] = useState<string | null>(null);

  // Fire-and-forget meting; nooit blokkerend, nooit een fout naar de
  // klant. Zelfde grens als de rest van de pagina.
  const meet = (stap: HerbestelMeetStap) => {
    try {
      void herbestelApi.meetStap(token, shopToken, stap).catch(() => {});
    } catch {
      // Meten is nooit blokkerend; een gemiste meting is geen fout.
    }
  };

  const kiesMethode = (code: string) => {
    if (code === methode) {
      setBetaalOpen(true);
      return;
    }
    setMethode(code);
    setBetaalOpen(true);
    try {
      void herbestelApi
        .meetMethode(token, shopToken, code)
        .catch(() => {});
    } catch {
      // Meten is nooit blokkerend.
    }
  };

  useEffect(() => {
    let actief = true;

    // Zonder herkenning is er niets op te halen: meteen door naar het
    // aanvraagscherm (of de kale melding als dat uit staat).
    if (!token && !shopToken) {
      herbestelApi
        .stand()
        .then((stand) => {
          if (!actief) return;
          setAanvraagMogelijk(stand.aanvraag_mogelijk);
          setGeldigUren(stand.aanvraag_geldigheid_uren);
          setWeergave(stand.aanvraag_mogelijk ? "aanvraag" : "ongeldig");
        })
        .catch(() => {
          if (actief) setWeergave("ongeldig");
        });
      return () => {
        actief = false;
      };
    }

    // Token bewaren voor de "Opnieuw proberen"-knop op de
    // bevestigingspagina — sessionStorage, nooit een URL of server.
    if (token) {
      try {
        sessionStorage.setItem(HERBESTEL_TOKEN_OPSLAG, token);
      } catch {
        // Private mode zonder opslag: dan geen opnieuw-knop, meer niet.
      }
    }

    herbestelApi
      .pagina(token, shopToken)
      .then((antwoord) => {
        if (!actief) return;
        setData(antwoord);
        setAantallen(
          Object.fromEntries(
            antwoord.regels.map((r) => [r.vid, r.aantal]),
          ),
        );
        setAangevinkt(
          Object.fromEntries(
            antwoord.regels.map((r) => [r.vid, r.aangevinkt]),
          ),
        );
        setAlleProducten(false);
        const methodes = antwoord.ideal?.methodes ?? [];
        const voorkeur =
          methodes.find((m) => m.meest_gekozen) ?? methodes[0];
        if (voorkeur) {
          setMethode(voorkeur.code);
          // De voorselectie telt óók als "methode gekozen" (met
          // markering) — wie iDEAL laat staan en meteen betaalt zou
          // anders nooit in die trechterstap vallen.
          try {
            void herbestelApi
              .meetMethode(token, shopToken, voorkeur.code, true)
              .catch(() => {});
          } catch {
            // Meten is nooit blokkerend.
          }
        }
        setWeergave("pagina");
      })
      .catch(() => {
        if (!actief) return;
        setWeergave("ongeldig");
        herbestelApi
          .stand()
          .then((stand) => {
            if (!actief) return;
            setAanvraagMogelijk(stand.aanvraag_mogelijk);
            setGeldigUren(stand.aanvraag_geldigheid_uren);
          })
          .catch(() => undefined);
      });
    return () => {
      actief = false;
    };
  }, [token, shopToken]);

  const aantalVan = (vid: string) => aantallen[vid] ?? 1;

  // Een regel doet mee als hij is aangevinkt én het aantal minstens 1
  // is. Op 0 verdwijnt een product uit de bestelling zonder dat de
  // kaart zelf van de pagina verdwijnt.
  const gekozenRegels = useMemo(() => {
    if (!data) return [] as HerbestelRegelAntwoord[];
    return data.regels.filter((r) => doetMee(r, aantallen, aangevinkt));
  }, [data, aangevinkt, aantallen]);

  // De teller boven de lijst en (met de vlag uit) het totaal in de
  // somregel. Met het afrekenen op de pagina komt het échte totaal uit
  // de offerte; dit blijft de productteller.
  const { stuks: aantalProducten, totaalCents } = useMemo(
    () =>
      berekenTotalen(
        data?.regels ?? [],
        aantallen,
        aangevinkt,
        data?.combideal ?? null,
        combidealGekozen,
      ),
    [data, aantallen, aangevinkt, combidealGekozen],
  );

  const idealActief = data?.ideal?.actief === true;
  const betaalmethodes = useMemo(
    () => data?.ideal?.methodes ?? [],
    [data],
  );
  const zichtbareMethodes = useMemo(
    () =>
      adresModus === "ander"
        ? betaalmethodes.filter((m) => !m.alleen_eigen_adres)
        : betaalmethodes,
    [betaalmethodes, adresModus],
  );
  const gekozenMethode =
    zichtbareMethodes.find((m) => m.code === methode) ??
    zichtbareMethodes.find((m) => m.meest_gekozen) ??
    zichtbareMethodes[0] ??
    null;

  const betaalRegels = useMemo(() => {
    if (!data) return [] as { vid: string; aantal: number }[];
    const regels = gekozenRegels.map((r) => ({
      vid: r.vid,
      aantal: aantallen[r.vid] ?? 1,
    }));
    if (combidealGekozen && data.combideal) {
      regels.push({ vid: data.combideal.vid, aantal: 1 });
    }
    return regels;
  }, [data, gekozenRegels, aantallen, combidealGekozen]);

  // De invoer van de offerte. `null` zodra er niets te offreren valt:
  // dan staat de offerte-stand op "leeg" en toont het scherm geen
  // bedragen — beter dan een oud totaal dat nergens meer bij hoort.
  const offerteInvoer: OfferteInvoer | null = useMemo(() => {
    if (
      weergave !== "pagina" ||
      !idealActief ||
      betaalRegels.length === 0 ||
      !gekozenMethode
    ) {
      return null;
    }
    if (adresModus === "ander" && !bevestigdAdres) return null;
    return {
      regels: betaalRegels,
      combidealVid:
        combidealGekozen && data?.combideal ? data.combideal.vid : null,
      adresKeuze: adresModus,
      adres: adresModus === "ander" ? bevestigdAdres : null,
      methode: gekozenMethode.code,
      bezorgoptie,
      bezorgvervoerder,
      kortingscode: kortingCode,
    };
  }, [
    weergave,
    idealActief,
    betaalRegels,
    gekozenMethode,
    adresModus,
    bevestigdAdres,
    combidealGekozen,
    data,
    bezorgoptie,
    bezorgvervoerder,
    kortingCode,
  ]);

  const opOfferteFout = (soort: "fout" | "adres_onbekend") => {
    if (soort !== "adres_onbekend") return;
    // Het bekende adres mist een verplicht gegeven (doorgaans het
    // telefoonnummer). De pagina verraadt dat niet vooraf; hier
    // schakelt hij naar de invulweg met een rustige regel.
    setAdresModus("ander");
    setAdresOpen(true);
    setAdresHint(true);
  };

  const {
    stand: offerteStand,
    offerte,
    opnieuw: offerteOpnieuw,
  } = useOfferte(token, shopToken, offerteInvoer, opOfferteFout);

  // Trechterstap "afrekenscherm in beeld": op het moment dat de klant
  // een werkende offerte met een totaal voor zich heeft, precies één
  // keer per paginabezoek. Een scherm dat blijft hangen op de
  // wachtstand telt dus niet mee — dat is de bedoeling.
  const schermGemeten = useRef(false);
  useEffect(() => {
    if (offerteStand !== "klaar" || schermGemeten.current) return;
    schermGemeten.current = true;
    meet("scherm_geopend");
    // `meet` is een stabiele closure over token/shopToken.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerteStand]);

  const afrekenen = () => {
    if (!data || bezig || aantalProducten === 0) return;
    setBezig(true);
    setAfrekenFout(false);
    herbestelApi
      .afrekenen(
        token,
        shopToken,
        betaalRegels,
        combidealGekozen && data.combideal ? data.combideal.vid : null,
      )
      .then((antwoord) => {
        window.location.assign(antwoord.shop_url);
      })
      .catch(() => {
        setAfrekenFout(true);
        setBezig(false);
      });
  };

  const betalen = () => {
    if (!data || bezig || !offerte || offerteStand !== "klaar") return;
    setBetaalFout(false);
    setBetaalVerouderd(false);
    setBezig(true);
    herbestelApi
      .betalen(
        token,
        shopToken,
        betaalRegels,
        combidealGekozen && data.combideal ? data.combideal.vid : null,
        adresModus,
        adresModus === "ander" ? bevestigdAdres : null,
        gekozenMethode?.code ?? "IDEAL",
        {
          // Zonder eigen keuze is de standaardmethode van dit mandje
          // voorgekozen; die gaat mee als expliciete keuze, zodat de
          // betaalrij dezelfde methode vastlegt die op het scherm
          // stond. Niet als state overgenomen: dat zou een tweede
          // waarheid naast de offerte zijn.
          bezorgoptie: bezorgoptie ?? offerte.gekozen_optie,
          // En de vervoerder erbij (17-09), zodat de betaalstart
          // dezelfde keuze vasthoudt als het scherm zodra Lightspeed
          // het id niet meer aanbiedt.
          bezorgvervoerder:
            bezorgvervoerder ??
            offerte.opties.find((o) => o.id === offerte.gekozen_optie)
              ?.titel ??
            null,
          kortingscode: kortingCode,
          // De vingerafdruk van precies het bedrag dat op de knop
          // stond. Geeft de verse offerte iets anders, dan weigert de
          // server met 409 en rekent er niets af.
          offerteAfdruk: offerte.afdruk,
        },
      )
      .then((antwoord) => {
        // Rechtstreeks naar de bank; de betaalstatus komt straks via
        // de webhook, nooit via deze browser. `bezig` blijft bewust
        // aan: de knop houdt zijn laadstand tot de pagina wegnavigeert.
        window.location.assign(antwoord.betaal_url);
      })
      .catch((fout: unknown) => {
        // Een afgebroken verzoek (de klant navigeert weg, tabblad
        // dicht) is geen fout — die zou een rode melding tonen op een
        // pagina die al verdwijnt.
        if ((fout as { code?: string } | null)?.code === "ERR_CANCELED") {
          return;
        }
        const status = (
          fout as { response?: { status?: number } } | null
        )?.response?.status;
        const detail = (
          fout as { response?: { data?: { detail?: string } } } | null
        )?.response?.data?.detail;
        setBezig(false);
        if (status === 409 && detail === "offerte_verouderd") {
          // Het bedrag klopte niet meer. Verse offerte halen en het de
          // klant zeggen; er is niets afgeschreven.
          setBetaalVerouderd(true);
          offerteOpnieuw();
          return;
        }
        if (status === 409) {
          opOfferteFout("adres_onbekend");
          return;
        }
        setBetaalFout(true);
      });
  };

  const bevestigAdres = () => {
    const compleet = Object.values(anderAdres).every(
      (v) => v.trim().length > 0,
    );
    if (!compleet) {
      setAdresFout(true);
      return;
    }
    setAdresFout(false);
    setAdresHint(false);
    setAdresModus("ander");
    setBevestigdAdres({ ...anderAdres });
    setAdresOpen(false);
    // Een ander land of een andere postcode kan een andere
    // bezorgmethode betekenen; de eigen keuze vervalt dus en de
    // standaard van het nieuwe adres wordt opnieuw voorgekozen.
    setBezorgoptie(null);
    meet("adres_gewijzigd");
  };

  const hoofd = data?.regels.find((r) => r.is_hoofd) ?? null;
  const subregels = data?.regels.filter((r) => !r.is_hoofd) ?? [];
  const hoofdUit = hoofd !== null && aantalVan(hoofd.vid) === 0;

  const zichtbareSubregels = alleProducten
    ? subregels
    : subregels.slice(0, MAX_SUBREGELS_INGEKLAPT);
  const verborgen = subregels.slice(zichtbareSubregels.length);
  const verborgenGekozen = verborgen.filter((r) =>
    doetMee(r, aantallen, aangevinkt),
  ).length;

  const heeftProducten = (data?.regels.length ?? 0) > 0;
  const punten = data
    ? Math.ceil(totaalCents / 100) * data.punten_per_euro
    : 0;
  const gratisVerzending = data
    ? totaalCents >= data.gratis_verzending_vanaf_cents
    : false;

  // De datum van de bestelling waar deze pagina op teruggrijpt: de
  // laatste keer dat er iets uit deze lijst besteld is.
  const laatsteBestelling = useMemo(() => {
    const datums = (data?.regels ?? [])
      .map((r) => r.laatst_besteld)
      .filter((d): d is string => Boolean(d));
    if (datums.length === 0) return null;
    return datumKort(datums.slice().sort().at(-1) ?? null);
  }, [data]);

  const voornaam = data?.voornaam ?? null;
  const kop =
    weergave === "pagina" && data
      ? idealActief
        ? voornaam
          ? t("herbestel.eenscherm_titel", { voornaam })
          : t("herbestel.eenscherm_titel_zonder_naam")
        : data.koptekst
      : weergave === "ongeldig"
        ? t("herbestel.ongeldig_titel")
        : t("herbestel.aanvraag_titel");

  // De wachtstand is alléén het verversen. "leeg" is geen wachtstand
  // (er valt niets te offreren), maar wel een stand waarin de betaalknop
  // uit moet — dat regelt `kanBetalen`.
  const offerteBezig = offerteStand === "bezig";
  const offerteFout = offerteStand === "fout";
  const kanBetalen =
    aantalProducten > 0 && offerteStand === "klaar" && offerte !== null;

  const adresFormulier = (
    <form
      data-testid="ander-adres-formulier"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        bevestigAdres();
      }}
      className="mt-3 rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
    >
      <p className="text-[15px] font-bold text-gray-900">
        {t("herbestel.adres_ander_titel")}
      </p>
      <p className="mt-1 text-[12.5px] leading-relaxed text-gray-500">
        {t("herbestel.adres_ander_uitleg")}
      </p>
      {adresHint && (
        <p
          role="status"
          data-testid="adres-hint"
          className="mt-2 text-[13px] leading-relaxed text-[#333333]"
        >
          {t("herbestel.adres_telefoon_ontbreekt")}
        </p>
      )}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {(
          [
            ["naam", "adres_naam", "name"],
            ["straat", "adres_straat", "address-line1"],
            ["huisnummer", "adres_huisnummer", "address-line2"],
            ["postcode", "adres_postcode", "postal-code"],
            ["plaats", "adres_plaats", "address-level2"],
            ["telefoon", "adres_telefoon", "tel"],
          ] as const
        ).map(([veld, label, autoComplete]) => (
          <label
            key={veld}
            className="block text-[13px] font-semibold text-[#333333]"
          >
            {t(`herbestel.${label}`)}
            <input
              type="text"
              autoComplete={autoComplete}
              value={anderAdres[veld]}
              onChange={(e) => {
                setAnderAdres((s) => ({ ...s, [veld]: e.target.value }));
                if (adresFout) setAdresFout(false);
              }}
              // 16px, niet 14: iOS zoomt in op een invoerveld met een
              // kleinere letter en schuift het scherm dan zijwaarts —
              // midden in het afrekenen. De globale regel in
              // globals.css sluit `.retour-shop` uit (eigen
              // shop-typografie), dus dit scherm regelt het zelf.
              className="mt-1 block w-full rounded-[10px] border border-gray-300 bg-white px-3.5 py-2.5 text-[16px] font-normal text-gray-900 outline-none focus:border-gray-400"
            />
          </label>
        ))}
      </div>
      {adresFout && (
        <p
          role="alert"
          className="mt-2 text-[13px] font-semibold text-red-600"
        >
          {t("herbestel.adres_onvolledig")}
        </p>
      )}
      <div className="mt-3.5 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          data-testid="adres-bevestigen"
          className="rounded-[10px] px-5 py-3 text-[14px] font-bold text-white"
          style={{ background: ORANJE }}
        >
          {t("herbestel.adres_bevestigen")}
        </button>
        {/* Terug naar het bekende adres kan alleen als dat er is. Is de
            klant hier omdat het bekende adres een gegeven mist, dan is
            er geen weg terug en zou de knop liegen. */}
        {bezorgPlaatsBekend(data) && !adresHint && (
          <button
            type="button"
            data-testid="adres-annuleren"
            onClick={() => {
              setAdresOpen(false);
              setAdresFout(false);
              if (!bevestigdAdres) setAdresModus("klant");
            }}
            className="rounded-sm text-[13.5px] font-medium text-blue-600 underline-offset-2 hover:underline"
          >
            {t("herbestel.adres_terug")}
          </button>
        )}
      </div>
    </form>
  );

  // De blokken dragen zelf geen bovenmarge meer: die zat het uitlijnen
  // van de twee kolommen in de weg (JP, 15-09). De afstand ertussen
  // staat hier, op één plek, en blijft 12 px zoals hij was.
  const rechterkolom = (
    <div className="grid gap-3">
      <Bezorgblok
        offerte={offerte}
        bezig={offerteBezig}
        fout={offerteFout}
        aangepast={offerte?.bezorgkeuze_aangepast ?? false}
        opKies={(id, titel) => {
          if (id === bezorgoptie) return;
          setBezorgoptie(id);
          // De titel is de sleutel die een mandjewijziging overleeft.
          setBezorgvervoerder(titel);
          meet("bezorgoptie_gewijzigd");
        }}
      />
      <Betaalblok
        methodes={zichtbareMethodes}
        gekozen={gekozenMethode?.code ?? methode}
        open={betaalOpen}
        bezig={offerteBezig}
        // Veiligheidsronde V1 (A1): achteraf betalen kan alleen op het
        // bekende adres. De methode verdwijnt dan uit de lijst — en de
        // uitleg staat op de plek waar hij ontbreekt, niet in het
        // adresformulier dat inmiddels weer dicht is.
        achterafWeg={
          adresModus === "ander" &&
          betaalmethodes.some((m) => m.alleen_eigen_adres)
        }
        opKies={kiesMethode}
        opOpen={() => setBetaalOpen(true)}
      />
      <Kortingsblok
        open={kortingOpen}
        waarde={kortingInvoer}
        toegepast={offerte?.kortingscode ?? null}
        werkt={offerte?.kortingscode_werkt ?? true}
        bezig={offerteBezig}
        opOpen={() => setKortingOpen(true)}
        opWijzig={setKortingInvoer}
        opToepassen={() => {
          const code = kortingInvoer.trim();
          if (!code || code === kortingCode) return;
          setKortingCode(code);
          meet("code_toegepast");
        }}
        opWissen={() => {
          setKortingInvoer("");
          setKortingCode(null);
        }}
      />
      <Totaalblok
        offerte={offerte}
        bezig={offerteBezig}
        fout={offerteFout}
        betaalBezig={bezig}
        geenProducten={aantalProducten === 0}
        kanBetalen={kanBetalen}
        opBetalen={betalen}
        opOpnieuw={offerteOpnieuw}
      />
      {betaalVerouderd && (
        <p
          role="alert"
          data-testid="betaal-verouderd"
          className="mt-2 text-[13px] font-semibold text-red-600"
        >
          {t("herbestel.betaal_verouderd")}
        </p>
      )}
      {betaalFout && (
        <p
          role="alert"
          data-testid="betaal-fout"
          className="mt-2 text-[13px] font-semibold text-red-600"
        >
          {t("herbestel.betaal_fout")}
        </p>
      )}
    </div>
  );

  return (
    <div className="retour-shop flex min-h-screen flex-col bg-white">
      {/* Kop: logo plus het WebwinkelKeur-cijfer klein. Geen USP-balk en
          geen verkooptekst — dit scherm is de checkout, en elke regel
          die niet over déze bestelling gaat duwt de betaalknop weg. */}
      <RetourHeader />
      {/* De USP-strip is winkelinformatie. Op het eenscherm gaat die
          ruimte naar het afrekenen zelf (mock 15-09: geen USP-balk, geen
          verkooptekst); met het afrekenen op de pagina uit blijft de
          pagina exact zoals hij was. */}
      {!idealActief && <UspBalk />}
      <main className={`${RETOUR_KOLOM} flex-1 py-6`}>
        {weergave === "laden" ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <section aria-label={kop}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <h1 className="text-2xl font-bold tracking-tight text-brand-navy">
                {kop}
              </h1>
              {!idealActief && (
                <p className="text-[13.5px] text-gray-500">
                  {t("herbestel.winkelen_vraag")}{" "}
                  <a
                    href={SHOP_URL}
                    className="rounded-sm font-medium text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t("herbestel.winkelen_link")}
                  </a>
                </p>
              )}
            </div>

            {weergave === "pagina" && data && (
              <p className="mt-1.5 text-[14px] leading-relaxed text-[#333333]">
                {idealActief
                  ? laatsteBestelling
                    ? t("herbestel.eenscherm_sub", {
                        datum: laatsteBestelling,
                      })
                    : t("herbestel.eenscherm_sub_zonder_datum")
                  : t("herbestel.sub")}
              </p>
            )}

            {/* Met het afrekenen op de shop staat de puntenbelofte
                bovenaan, zoals voorheen; op het eenscherm staat hij
                onder de bestelling, want daar hoort hij bij. */}
            {!idealActief && weergave === "pagina" && punten > 0 && (
              <span
                className="mt-2 inline-block rounded-full border px-3 py-1 text-[12.5px]"
                style={{
                  color: PUNTEN_KLEUR,
                  background: "#fff9f3",
                  borderColor: "#f7dfc2",
                }}
              >
                {t("herbestel.punten_badge", { punten })}
              </span>
            )}

            {/* Zelfde reden als de USP-balk: winkelinformatie hoort
                niet tussen de klant en zijn betaalknop. */}
            {!idealActief && (
              <VinkjesStrip
                label={t("herbestel.vinkjes_titel")}
                punten={[
                  t("herbestel.vinkje_klaar"),
                  t("herbestel.vinkje_checkout"),
                  t("herbestel.vinkje_beveiligd"),
                ]}
              />
            )}

            {(weergave === "aanvraag" || weergave === "ongeldig") && (
              <div className="mt-6 md:grid md:grid-cols-[minmax(0,1fr)_300px] md:items-start md:gap-6">
                <div className="min-w-0">
                  <div
                    data-testid={
                      weergave === "aanvraag"
                        ? "aanvraagscherm"
                        : "ongeldigscherm"
                    }
                    className={RETOUR_KAART}
                  >
                    {weergave === "ongeldig" && (
                      <p className="text-[14px] leading-relaxed text-[#333333]">
                        {aanvraagMogelijk
                          ? t("herbestel.ongeldig_uitleg_aanvraag")
                          : t("herbestel.ongeldig_uitleg")}
                      </p>
                    )}
                    {(weergave === "aanvraag" || aanvraagMogelijk) && (
                      <AanvraagFormulier
                        compact={weergave === "ongeldig"}
                        geldigUren={geldigUren}
                      />
                    )}
                    <p className="mt-4 text-[12.5px] leading-relaxed text-gray-500">
                      {t("herbestel.aanvraag_voet")}
                    </p>
                  </div>
                </div>
                <div className="mt-6 md:mt-0">
                  <VinkjesKaart
                    titel={t("herbestel.zowerkt_titel")}
                    punten={[
                      t("herbestel.zowerkt_inbox"),
                      t("herbestel.zowerkt_geldig", { uren: geldigUren }),
                      t("herbestel.zowerkt_klaar"),
                      t("herbestel.zowerkt_geen_gegevens"),
                    ]}
                  />
                </div>
              </div>
            )}

            {weergave === "pagina" && data && (
              <>
                {data.regels.length === 0 && (
                  <div className="mt-5 rounded-xl border border-gray-200 bg-white p-8 text-center">
                    <p className="text-[14px] leading-relaxed text-[#333333]">
                      {t("herbestel.leeg")}
                    </p>
                  </div>
                )}

                {/* Adresregel bovenaan: de klant ziet meteen waar het
                    heen gaat, en kan het in één tik wijzigen. */}
                {idealActief && heeftProducten && (
                  <>
                    <Adresregel
                      // Bij een zelf ingevuld adres komt de regel uit
                      // de invoer en niet uit de offerte: die is nog
                      // aan het verversen, en het oude adres laten
                      // staan terwijl de klant net een nieuw invulde
                      // leest als "mijn wijziging is niet aangekomen".
                      // Het is hetzelfde adres — de offerte wordt er
                      // mee opgebouwd — alleen eerder in beeld.
                      regel={
                        adresModus === "ander" && bevestigdAdres
                          ? adresRegelVan(bevestigdAdres)
                          : (offerte?.bezorgadres.regel ??
                            bezorgPlaatsBekend(data))
                      }
                      herkomst={adresModus}
                      bezig={bezig}
                      opWijzig={() => {
                        setAdresOpen((open) => !open);
                        setAdresFout(false);
                      }}
                    />
                    {adresOpen && adresFormulier}
                  </>
                )}

                {/* Desktop: bestelling links, afrekenen rechts. Mobiel
                    stapelt alles in leesvolgorde, de mock-volgorde. */}
                <div
                  className={
                    idealActief && heeftProducten
                      ? // Even breed (JP, 15-09): met 60/40 lag de
                        // rechterkolom optisch los van de bestelling.
                        // Mobiel stapelt en blijft ongewijzigd.
                        "md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:items-start md:gap-6"
                      : ""
                  }
                >
                  <div className="min-w-0">
                    {hoofd && (
                      <div
                        data-testid="hoofdkaart"
                        data-uit={hoofdUit ? "ja" : "nee"}
                        /* De badge staat binnen de kaartrand (JP,
                           15-09): stak hij eroverheen, dan begon deze
                           kolom optisch hoger dan de rechter en lagen
                           de twee kaarten nooit gelijk. */
                        className={`relative mt-4 flex flex-wrap items-center gap-4 rounded-xl bg-white px-5 pb-5 pt-9 ${
                          hoofdUit ? "opacity-75" : ""
                        }`}
                        style={{
                          border: `2px solid ${hoofdUit ? "#c3ccd3" : ORANJE}`,
                        }}
                      >
                        <span
                          className="absolute left-4 top-3 rounded-full px-3 py-0.5 text-[11px] font-bold tracking-wide text-white"
                          style={{
                            background: hoofdUit ? GRIJS : ORANJE,
                          }}
                        >
                          {t("herbestel.hoofdlabel")}
                        </span>
                        <ProductFoto url={hoofd.afbeelding_url} />
                        <span className="min-w-[180px] flex-1">
                          <span className="block text-[15.5px] font-bold leading-snug text-gray-900">
                            {hoofd.naam}
                          </span>
                          <span className="mt-0.5 block text-[12.5px] text-gray-500">
                            {t("herbestel.eerder_besteld", {
                              count: hoofd.frequentie,
                            })}
                            {datumKort(hoofd.laatst_besteld)
                              ? ` · ${t("herbestel.laatst_op", {
                                  datum: datumKort(hoofd.laatst_besteld),
                                })}`
                              : ""}
                          </span>
                          <span
                            className="mt-0.5 block text-[12.5px] font-bold"
                            style={{ color: GROEN }}
                          >
                            ✓ {t("herbestel.op_voorraad")}
                          </span>
                        </span>
                        <span className="ml-auto flex items-center gap-3">
                          <Stepper
                            waarde={aantalVan(hoofd.vid)}
                            opWijzig={(n) =>
                              setAantallen((s) => ({
                                ...s,
                                [hoofd.vid]: n,
                              }))
                            }
                            label={hoofd.naam}
                            minimum={0}
                          />
                          <RegelPrijs
                            prijsCents={hoofd.prijs_cents}
                            aantal={aantalVan(hoofd.vid)}
                            gedempt={hoofdUit}
                            breed
                          />
                        </span>
                      </div>
                    )}

                    {zichtbareSubregels.map((regel) => {
                      const aan = aangevinkt[regel.vid] ?? false;
                      return (
                        <div
                          key={regel.vid}
                          data-testid={`subkaart-${regel.vid}`}
                          className={`mt-3 flex flex-wrap items-center gap-3.5 rounded-[10px] border border-gray-200 bg-white px-4 py-3 ${
                            aan ? "" : "opacity-75"
                          }`}
                        >
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={aan}
                            aria-label={regel.naam}
                            onClick={() =>
                              setAangevinkt((s) => ({
                                ...s,
                                [regel.vid]: !aan,
                              }))
                            }
                            className="grid h-6 w-6 shrink-0 place-items-center rounded-[6px] border"
                            style={
                              aan
                                ? {
                                    background: GROEN,
                                    borderColor: GROEN,
                                    color: "#fff",
                                  }
                                : {
                                    background: "#fff",
                                    borderColor: "#c3ccd3",
                                  }
                            }
                          >
                            {aan && <Check className="h-4 w-4" />}
                          </button>
                          <ProductFoto url={regel.afbeelding_url} />
                          <span className="min-w-[150px] flex-1">
                            <span className="block text-[13.5px] font-bold leading-snug text-gray-900">
                              {regel.naam}
                            </span>
                            <span className="mt-0.5 block text-[12px] text-gray-500">
                              {t("herbestel.eerder_besteld", {
                                count: regel.frequentie,
                              })}
                            </span>
                          </span>
                          <span className="ml-auto flex items-center gap-3">
                            {aan && (
                              <Stepper
                                waarde={aantalVan(regel.vid)}
                                opWijzig={(n) =>
                                  setAantallen((s) => ({
                                    ...s,
                                    [regel.vid]: n,
                                  }))
                                }
                                label={regel.naam}
                              />
                            )}
                            <RegelPrijs
                              prijsCents={regel.prijs_cents}
                              aantal={aan ? aantalVan(regel.vid) : 1}
                              gedempt={!aan}
                            />
                          </span>
                        </div>
                      );
                    })}

                    {verborgen.length > 0 && (
                      <button
                        type="button"
                        data-testid="toon-alle-producten"
                        onClick={() => setAlleProducten(true)}
                        className="mt-3 w-full rounded-[10px] border border-gray-200 bg-white px-4 py-3 text-[13.5px] font-semibold text-gray-700 hover:bg-slate-50"
                      >
                        {verborgenGekozen > 0
                          ? t("herbestel.toon_alle_met_gekozen", {
                              count: subregels.length,
                              gekozen: verborgenGekozen,
                            })
                          : t("herbestel.toon_alle_producten", {
                              count: subregels.length,
                            })}
                      </button>
                    )}
                    {alleProducten &&
                      subregels.length > MAX_SUBREGELS_INGEKLAPT && (
                        <button
                          type="button"
                          data-testid="toon-minder-producten"
                          onClick={() => setAlleProducten(false)}
                          className="mt-3 rounded-sm text-[13px] font-medium text-blue-600 underline-offset-2 hover:underline"
                        >
                          {t("herbestel.toon_minder_producten")}
                        </button>
                      )}

                    {data.combideal && (
                      <div
                        data-testid="combideal"
                        className="relative mt-5 flex flex-wrap items-center gap-4 rounded-xl p-5"
                        style={{
                          border: `1.5px dashed ${BLAUW}`,
                          background: "#f4fbfe",
                        }}
                      >
                        <span
                          className="absolute -top-2.5 left-4 rounded-full px-3 py-0.5 text-[11px] font-bold tracking-wide text-white"
                          style={{ background: BLAUW }}
                        >
                          {t("herbestel.combideal_label", {
                            pct: data.combideal.kortingspercentage,
                          })}
                        </span>
                        <ProductFoto url={data.combideal.afbeelding_url} />
                        <span className="min-w-[180px] flex-1">
                          <span className="block text-[14.5px] font-bold leading-snug text-gray-900">
                            {data.combideal.naam}
                          </span>
                          <span className="mt-0.5 block text-[12.5px] text-gray-500">
                            {t("herbestel.combideal_meta")}
                          </span>
                          <span
                            className="mt-0.5 block text-[12.5px] font-bold"
                            style={{ color: GROEN }}
                          >
                            ✓ {t("herbestel.combideal_prijs")}{" "}
                            <s className="font-normal text-gray-400">
                              {euro(data.combideal.prijs_cents)}
                            </s>{" "}
                            <strong>
                              {euro(
                                data.combideal.prijs_met_korting_cents ??
                                  data.combideal.prijs_cents,
                              )}
                            </strong>
                          </span>
                          {/* Eén korting tegelijk: een checkout draagt
                              maar één code. Loopt er een kortingscode
                              mee, dan vervalt de combikorting — dat
                              zeggen we hier, in plaats van het bedrag
                              stil te laten veranderen. */}
                          {combidealGekozen &&
                            offerte?.kortingscode != null && (
                              <span
                                data-testid="combideal-vervallen"
                                className="mt-1 block text-[12px] text-gray-500"
                              >
                                {t("herbestel.combideal_vervalt")}
                              </span>
                            )}
                        </span>
                        <button
                          type="button"
                          data-testid="combideal-knop"
                          aria-pressed={combidealGekozen}
                          onClick={() => {
                            setCombidealGekozen((v) => {
                              if (!v) meet("combideal_klik");
                              return !v;
                            });
                          }}
                          className="ml-auto rounded-lg px-4 py-2.5 text-[13px] font-bold"
                          style={
                            combidealGekozen
                              ? {
                                  background: BLAUW,
                                  border: `2px solid ${BLAUW}`,
                                  color: "#fff",
                                }
                              : {
                                  background: "#fff",
                                  border: `2px solid ${BLAUW}`,
                                  color: "#2596be",
                                }
                          }
                        >
                          {combidealGekozen
                            ? `✓ ${t("herbestel.combideal_toegevoegd")}`
                            : `+ ${t("herbestel.combideal_toevoegen")}`}
                        </button>
                      </div>
                    )}

                    {/* Vlag ideal_afrekenen uit: de opbouw van vóór deze
                        ronde blijft precies zoals hij was — de som met
                        de knop die de webshop-wagen vult. */}
                    {!idealActief && data.regels.length > 0 && (
                      <>
                        <div
                          data-testid="somregel"
                          className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-4"
                        >
                          <span className="text-[14px] text-[#333333]">
                            {t("herbestel.som_producten", {
                              count: aantalProducten,
                            })}{" "}
                            ·{" "}
                            <strong className="text-[17px] text-gray-900">
                              {euro(totaalCents)}
                            </strong>
                            {gratisVerzending && (
                              <>
                                {" "}
                                ·{" "}
                                <span
                                  className="font-bold"
                                  style={{ color: GROEN }}
                                >
                                  {t("herbestel.gratis_verzending")}
                                </span>
                              </>
                            )}
                            {punten > 0 && (
                              <>
                                {" "}
                                ·{" "}
                                <span style={{ color: PUNTEN_KLEUR }}>
                                  {t("herbestel.plus_punten", { punten })}
                                </span>
                              </>
                            )}
                          </span>
                          <button
                            type="button"
                            data-testid="afrekenen"
                            disabled={bezig || aantalProducten === 0}
                            onClick={afrekenen}
                            className="ml-auto rounded-[10px] px-6 py-3.5 text-[15px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-80"
                            style={{
                              background:
                                aantalProducten === 0 ? GRIJS : ORANJE,
                            }}
                          >
                            {bezig ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : aantalProducten === 0 ? (
                              t("herbestel.kies_minstens")
                            ) : (
                              t("herbestel.direct_afrekenen")
                            )}
                          </button>
                        </div>
                        {afrekenFout && (
                          <p
                            role="alert"
                            className="mt-2 text-[13px] font-semibold text-red-600"
                          >
                            {t("herbestel.afreken_fout")}
                          </p>
                        )}
                        <p className="mt-2.5 text-[12.5px] leading-relaxed text-gray-500">
                          <span
                            className="mr-1 font-bold"
                            style={{ color: GROEN }}
                          >
                            ✓
                          </span>
                          {t("herbestel.hint")}
                        </p>
                      </>
                    )}

                    {/* De spaarpuntenbelofte hoort bij de bestelling,
                        niet bij het afrekenblok: het is geen bedrag dat
                        in het totaal meetelt. */}
                    {idealActief && punten > 0 && (
                      <span
                        className="mt-4 inline-block rounded-full border px-3 py-1 text-[12.5px]"
                        style={{
                          color: PUNTEN_KLEUR,
                          background: "#fff9f3",
                          borderColor: "#f7dfc2",
                        }}
                      >
                        {t("herbestel.punten_badge", { punten })}
                      </span>
                    )}
                  </div>

                  {idealActief && heeftProducten && (
                    // Dezelfde bovenmarge als de eerste kaart links
                    // (mt-4), zodat de twee kaartranden op de pixel
                    // gelijk liggen. Mobiel houdt de afstand die het
                    // had: 16 px marge plus de 12 px die het bezorgblok
                    // zelf droeg.
                    <div className="mt-7 min-w-0 md:mt-4 md:sticky md:top-4">
                      {rechterkolom}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Nova staat onderaan: hulp hoort onder het afrekenen, niet
                ertussen. */}
            <NovaHulpKaart tekst={t("herbestel.nova_tekst")} />
          </section>
        )}
      </main>
      <RetourFooter />
    </div>
  );
}

/**
 * "Kerkstraat 9, 1234 AB Utrecht" uit een zelf ingevuld bezorgadres.
 *
 * Dezelfde vorm als de server op `bezorgadres.regel` zet, zodat het
 * adres tijdens het verversen niet van vorm verspringt.
 */
function adresRegelVan(adres: HerbestelAnderAdres): string {
  const straat = `${adres.straat} ${adres.huisnummer}`.trim();
  const plaats = `${adres.postcode} ${adres.plaats}`.trim();
  return [straat, plaats].filter(Boolean).join(", ");
}

/**
 * De woonplaats van het bekende adres, of null.
 *
 * Alleen om te weten óf er een bekend adres is — de volledige regel
 * komt uit de offerte, want alleen die is met het echte adres opgebouwd.
 */
function bezorgPlaatsBekend(
  data: HerbestelPaginaAntwoord | null,
): string | null {
  return data?.ideal?.bezorg_plaats ?? null;
}
